import React, { useEffect, useMemo, useState } from 'react';
import { Receipt, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { KpiCard } from '../../components/ui/KpiCard';
import { Badge } from '../../components/ui/Badge';
import { Table } from '../../components/ui/Table';
import { syncTimestampColumns } from '../../components/ui/SyncTimestamps';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '../../components/ui/SectionCard';
import { BarChart } from '../../components/ui/BarChart';
import api from '../../services/api';
import { INVOICE_STATUS, fmtDate, fmtAmount, groupByMonth } from '../../utils/dashboardUtils';
import { DashHeader, DashError, StatusBreakdown, MiniEmpty } from './DashboardShared';

const OVERDUE_COLS = [
  {
    key: 'reference',
    label: 'N° Facture',
    render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
  },
  {
    key: 'shipToName',
    label: 'Client',
    render: (v) => <span className="font-medium text-slate-700">{v || '—'}</span>,
  },
  {
    key: 'dueDate',
    label: 'Échéance',
    render: (v) => <span className="text-slate-600 text-[12px]">{v}</span>,
  },
  {
    key: 'ageDays',
    label: 'Retard',
    align: 'right',
    render: (v, row) =>
      v > 0 ? (
        <span className={`tabular-nums font-semibold text-[12px] ${row.statusVariant === 'overdue60' ? 'text-rose-600' : 'text-orange-600'}`}>
          +{v} j
        </span>
      ) : <span className="text-slate-400">—</span>,
  },
  {
    key: 'amountDisplay',
    label: 'Montant',
    align: 'right',
    render: (v) => <span className="tabular-nums font-semibold text-slate-800 text-[12.5px]">{v}</span>,
  },
];

const PENDING_COLS = [
  {
    key: 'reference',
    label: 'N° Facture',
    render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
  },
  {
    key: 'shipToName',
    label: 'Client',
    render: (v) => <span className="font-medium text-slate-700">{v || '—'}</span>,
  },
  {
    key: 'issuedAt',
    label: 'Émise le',
    render: (v) => <span className="text-slate-500 text-[12px]">{v}</span>,
  },
  {
    key: 'statusVariant',
    label: 'Statut',
    render: (v, row) => <Badge variant={v}>{row.statusLabel}</Badge>,
  },
  {
    key: 'amountDisplay',
    label: 'Montant',
    align: 'right',
    render: (v) => <span className="tabular-nums font-semibold text-slate-800 text-[12.5px]">{v}</span>,
  },
];

export default function FinanceDashboard() {
  const { t, i18n } = useTranslation();
  const timestampColumns = syncTimestampColumns(t, i18n.language);
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  function load() {
    setLoading(true);
    setError(null);
    api.get('/invoices?limit=1000')
      .then((r) => setInvoices(r.data.rows ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const enriched = useMemo(() => invoices.map((inv) => {
    const mapped = INVOICE_STATUS[inv.status] || { variant: 'info', label: inv.status || '—', group: 'other' };
    return {
      ...inv,
      statusVariant: mapped.variant,
      statusLabel:   mapped.label,
      statusGroup:   mapped.group,
      issuedAt:      fmtDate(inv.issuedAt),
      dueDate:       fmtDate(inv.dueDate),
      amountDisplay: fmtAmount(inv.amountTTC ?? inv.amountEur, inv.fretCurrencyCode || 'EUR'),
      ageDays:       inv.ageDays || 0,
      shipToName:    inv.shipToName || inv.billToCustomerNo || '—',
    };
  }), [invoices]);

  const kpis = useMemo(() => {
    const pending = enriched.filter((r) => r.statusGroup === 'pending');
    const paid    = enriched.filter((r) => r.statusGroup === 'paid');
    const overdue = enriched.filter((r) => r.statusGroup === 'overdue');
    const sumOf   = (arr) => arr.reduce((s, r) => s + (r.amountTTC ?? r.amountEur ?? 0), 0);
    const currency = enriched[0]?.fretCurrencyCode || 'EUR';
    return {
      total:         enriched.length,
      totalAmount:   sumOf(enriched),
      pendingCount:  pending.length,
      pendingAmount: sumOf(pending),
      paidCount:     paid.length,
      overdueCount:  overdue.length,
      overdueAmount: sumOf(overdue),
      paidPct:       enriched.length ? Math.round((paid.length / enriched.length) * 100) : 0,
      currency,
    };
  }, [enriched]);

  const monthlyData  = useMemo(() => groupByMonth(invoices, 'issuedAt'), [invoices]);
  const overdueRows  = useMemo(() => enriched.filter((r) => r.statusGroup === 'overdue').slice(0, 5), [enriched]);
  const pendingRows  = useMemo(() => enriched.filter((r) => r.statusGroup === 'pending').slice(0, 5), [enriched]);

  const statusItems = [
    { label: 'En attente', count: kpis.pendingCount, variant: 'pending'   },
    { label: 'Payées',     count: kpis.paidCount,    variant: 'paid'      },
    { label: 'En retard',  count: kpis.overdueCount, variant: 'overdue30' },
  ];

  return (
    <div className="flex flex-col gap-5">
      <DashHeader
        title="Tableau de bord — Finance"
        sub={`Bonjour ${user?.firstName} — ${enriched.length} facture${enriched.length !== 1 ? 's' : ''} au total`}
        onRefresh={load}
        loading={loading}
      />

      {error && <DashError message={error} onRetry={load} />}

      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          label="Facturation totale"
          value={loading ? '—' : kpis.total}
          sub={!loading ? fmtAmount(kpis.totalAmount, kpis.currency) : undefined}
          icon={Receipt}
        />
        <KpiCard
          label="En attente"
          value={loading ? '—' : kpis.pendingCount}
          sub={!loading && kpis.pendingAmount > 0 ? fmtAmount(kpis.pendingAmount, kpis.currency) : undefined}
        />
        <KpiCard
          label="En retard"
          value={loading ? '—' : kpis.overdueCount}
          sub={!loading && kpis.overdueAmount > 0 ? fmtAmount(kpis.overdueAmount, kpis.currency) : undefined}
          icon={AlertCircle}
          trend={kpis.overdueCount > 0 ? 'down' : undefined}
          trendLabel={kpis.overdueCount > 0 ? `${kpis.overdueCount} à régulariser` : 'Aucun retard'}
        />
        <KpiCard
          label="Taux de paiement"
          value={loading ? '—' : `${kpis.paidPct} %`}
          sub={!loading ? `${kpis.paidCount} facture${kpis.paidCount !== 1 ? 's' : ''} payée${kpis.paidCount !== 1 ? 's' : ''}` : undefined}
          trend={!loading && kpis.paidPct >= 80 ? 'up' : undefined}
          trendLabel={!loading && kpis.paidPct >= 80 ? 'Bonne santé' : undefined}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <SectionCard title="Factures émises par mois" className="col-span-2">
          {!loading && <BarChart data={monthlyData} barColor="#0F3D5C" />}
          {loading && <div className="h-[68px] bg-slate-100 rounded animate-pulse" />}
        </SectionCard>
        <SectionCard title="Répartition par statut">
          {loading
            ? <div className="space-y-2.5">{[1, 2, 3].map((i) => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}</div>
            : <StatusBreakdown items={statusItems} total={kpis.total} />
          }
        </SectionCard>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SectionCard
          title="Factures en retard"
          action={
            overdueRows.length > 0 && (
              <span className="text-[11px] font-semibold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">
                {overdueRows.length}
              </span>
            )
          }
        >
          {overdueRows.length > 0
            ? <Table columns={[...OVERDUE_COLS, ...timestampColumns]} rows={overdueRows} rowKey="_id" />
            : <MiniEmpty label="Aucune facture en retard" ok />
          }
        </SectionCard>

        <SectionCard
          title="Factures en attente"
          action={
            <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
              {pendingRows.length}
            </span>
          }
        >
          {pendingRows.length > 0
            ? <Table columns={[...PENDING_COLS, ...timestampColumns]} rows={pendingRows} rowKey="_id" />
            : <MiniEmpty label="Aucune facture en attente" ok />
          }
        </SectionCard>
      </div>
    </div>
  );
}
